'use client';

import { useEffect, useState } from 'react';

/** Mirrors the Tailwind `sm:` breakpoint the banner images swap on. */
const MOBILE_VIEWPORT_QUERY = '(max-width: 639px)';

/**
 * Which banner image should drive a series/course card's banner-derived colors
 * (dominant-color background + contrasting text) for the current viewport.
 *
 * The banner IMAGES already swap per-viewport via CSS (`sm:hidden` /
 * `hidden sm:block`), but the color probe used to sample only the desktop
 * banner — so a card with a separate mobile banner was tinted with the
 * DESKTOP banner's palette on phones (QA). The probe source must follow the
 * same breakpoint as the image swap so the sampled color always matches the
 * banner actually painted.
 *
 * SSR-safe: desktop-first until the first client effect — harmless, because
 * the dominant-color probe itself is client-only (canvas sampling) and the
 * card paints neutral defaults until it resolves. Re-selects live when the
 * viewport crosses the breakpoint.
 */
export function useBannerColorSourceUrl(
  desktopBannerUrl: string | null,
  mobileBannerUrl: string | null,
): string | null {
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    setIsMobileViewport(mediaQuery.matches);
    const onChange = (event: MediaQueryListEvent) =>
      setIsMobileViewport(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return isMobileViewport
    ? (mobileBannerUrl ?? desktopBannerUrl)
    : (desktopBannerUrl ?? mobileBannerUrl);
}
